/* eslint-disable max-lines-per-function */
/* eslint-disable prefer-object-spread */
export default class BopisAPI {

	/**
	 * Constructor of `BopisAPI`
	 */
	constructor (allowedStores) {
		this.allowedStores = allowedStores ?? [];
		const host = window.location.host;
		this.domain = (/^(dev)\./).test(host) ? host.replace(/^dev\./, 'www.') : host;

		this.MI_TO_KM = 1.60934;
		this.MI_TO_METER = this.MI_TO_KM * 1000;
		this.defaultRadius = this.MI_TO_METER * 9999;
		this.defaultMax = 9999;
		this.apiDaysList = [
			'Mon',
			'Tue',
			'Wed',
			'Thu',
			'Fri',
			'Sat',
			'Sun'
		];
		this.dayOfWeek = new Date().toLocaleString('en-us', {weekday: 'short'});

		this.storeLocatorPath = window.mivaJS.storeLocatorPath;
		this.storeSiteDomain = window.mivaJS?.Store_Locator_Key;
		this.mivaJsonAPIUrl = window.mivaJS?.Json_Url;
		this.geocodeKey = window.GOOGLE_GEOCODE_API_KEY;
		this.geocodeEndpoint = `https://maps.googleapis.com/maps/api/js?key=${this.geocodeKey}`;
		this.geocoder = null;
		this.mapsLoaded = false;

		this.cache = {
			bopisAPI: new Map(),
			geocode: new Map(),
			storeLookup: new Map()
		};

		this.rawLocationsCache = {};

		this.defaultParameters = this.#getDefaultParams();
	}

	/**
	 * Default Parameters
	 *
	 * Default lat and long are the Rally House Sampler store 1.
	 *
	 * @returns {Object}
	 */
	#getDefaultParams () {
		return {
			identifier: '',
			latitude: 37.73902,
			limit: 0,
			longitude: -97.24696,
			radius: 99999,
			siteDomain: this.storeSiteDomain
		};
	}

	/**
	 * Ensure Google Maps JS API is loaded and geocoder initialized
	 */
	async initGoogleMapsJS () {
		if (!this.mapsLoaded) {
			await this.loadGoogleMaps();
			this.initGeocoder();
			this.mapsLoaded = true;
		}
	}

	/**
	 * Dynamically load Google Maps JS API
	 * @returns {Promise<void>}
	 */
	async loadGoogleMaps () {
		if (window.google && window.google.maps) {
			// Already loaded
			return;
		}

		await new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = this.geocodeEndpoint;
			script.async = true;
			script.defer = true;

			script.onload = resolve;
			script.onerror = () => reject(new Error('Failed to load Google Maps API'));

			document.head.appendChild(script);
		});
	}

	/**
	 * Initialize the geocoder
	 */
	initGeocoder () {
		if (!window.google || !window.google.maps) {
			throw new Error('Google Maps API not loaded.');
		}
		this.geocoder = new window.google.maps.Geocoder();
	}

	/**
	 * Looks up store by it's identifier
	 * @param  {String} identifier - Store identifier
	 * @returns {Object} Store, if found.
	 */
	async singleLocationLookup (identifier) {
		const params = structuredClone(this.defaultParameters);
		params.identifier = identifier;

		// If User has allowed geolocation, utilize their lat + lng
		const geolocationPermissions = await navigator.permissions.query({name: 'geolocation'});
		if (geolocationPermissions.state === 'granted') {
			const getLocation = await new Promise((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(resolve, reject);
			}).then((position) => {
				return position;
			}).catch(() => {
				return false;
			});

			if (getLocation !== false) {
				params.latitude = getLocation.coords.latitude;
				params.longitude = getLocation.coords.longitude;
			}
		}

		const response = await this.request(params);

		return response !== false && response.length === 1 ? response[0] : false;
	}

	/**
	 * Utilize the `geocodeEndpoint` to do an API call.
	 * @param  {Object} params
	 * @returns {Object} Lookup Results, if found.
	 */
	async geocodeLookup (params) {

		await this.initGoogleMapsJS();

		return new Promise((resolve, reject) => {
			this.geocoder.geocode(params, (results, status) => {
				if (status === 'OK') {
					resolve(results[0]);
				} else {
					reject(new Error('Geocode failed: ' + status));
				}
			});
		});
	}

	/**
	 * Utilize the `geocodeEndpoint` to do an API call.
	 * @param  {String} term
	 * @returns {Object} Lookup Results, if found.
	 */
	async termLocationLookup (term) {
		const locationLookup = await this.geocodeLookup({
			address: term,
			componentRestrictions: {country: 'US'}
		});

		return locationLookup;
	}

	/**
	 * Looks up Lat/Lng via geocode API to return a place. Will be used in conjunction with a user sharing their location with the site.
	 * @param  {Number} lat - Latitude
	 * @param  {Number} lng - Longitude
	 * @returns {Object} Location if found
	 */
	async coordinatesLookup (lat, lng) {
		const params = structuredClone(this.defaultParameters);
		params.lat = lat;
		params.lng = lng;

		const locationLookup = await this.geocodeLookup({
			location: {
				lat: lat,
				lng: lng
			}
		});

		return locationLookup;
	}

	/**
	 * API Call to look up a store based on the lat/lng.
	 * @param  {Number} lat - Latitude
	 * @param  {Number} lng - Longitude
	 * @returns {Array} Locations
	 */
	async storeSearch (lat, lng, radius = 99999) {
		const params = structuredClone(this.defaultParameters);

		params.radius = radius;
		if (lat && lng) {
			params.latitude = lat;
			params.longitude = lng;
		}

		const response = await this.request(params);

		if (response === false) {
			return false;
		}

		return response;
	}

	/**
	 * API Call to look up a store based on the lat/lng, with no radius limit
	 * @param  {Number} lat - Latitude
	 * @param  {Number} lng - Longitude
	 * @returns {Object} Location, if found.
	 */
	async storeSearchNoRadius (lat, lng) {
		const params = structuredClone(this.defaultParameters);
		if (lat && lng) {
			params.latitude = lat;
			params.longitude = lng;
		}

		const response = await this.request(params);

		if (response === false) {
			return false;
		}

		return response[0] ? response[0] : false;
	}

	/**
	 * API Call to look up a store based on the lat/lng. Determines which would should display based on the Product's inventories.
	 * @param   {Number} lat - Latitude
	 * @param   {Number} lng - Longitude
	 * @param   {Object} productInventories - Object of Product Inventories
	 * @param   {Number} quantity - Quantity to check against. Default: 1.
	 * @returns {Array} Locations.
	 */
	async productStoreSearch (lat, lng, productInventories = {}, quantity = 1) {
		// Check MozSOCi, then ping miva api and cross reference the store `identifiers`
		const locations = await this.storeSearch(lat, lng);

		if (locations === false) {
			return false;
		}

		return this.productInventoryStores(productInventories, locations, quantity);
	}

	/**
	 * Determines if the locations are eligible based on the product's inventories.
	 * @param  {Object} inventories
	 * @param  {Object} storeLocations
	 * @param   {Number} quantity - Quantity to check against. Default: 1.
	 * @returns {Array} Locations
	 */
	productInventoryStores (inventories = {}, storeLocations = {}, quantity = 1) {
		const locations = [];

		for (const store of storeLocations) {
			store.inventoryAvailable = this.productInventoryStore(inventories, store, quantity);
			locations.push(store);
		}

		return locations;
	}

	/**
	 * Determine's if the store is on the "Allowed List" for BOPIS.
	 * @param  {Object} storeLocation
	 * @returns {Boolean}
	 */
	storeIsAllowed (storeLocation = {}) {
		return this.identifierIsAllowed(storeLocation?.identifier);
	}

	identifierIsAllowed (identifier) {
		return this.allowedStores.includes(identifier.toString());
	}

	/**
	 * Checks to see how much inventory is at the current location, based on the product's inventory object.
	 * @param  {Object} inventories- {"95": 9,"107": 1}
	 * @param  {Object} storeLocation - Moz SOCi Location
	 * @param   {Number} quantity - Quantity to check against. Default: 1.
	 * @returns {Boolean}
	 */
	productInventoryStore (inventories = {}, storeLocation, quantity = 1) {
		storeLocation.productInventory = 0;

		if (inventories.hasOwnProperty(storeLocation.identifier)) {
			storeLocation.productInventory = parseInt(inventories[storeLocation.identifier], 10);
		}

		storeLocation.storeIsAllowed = this.storeIsAllowed(storeLocation);

		return Boolean(storeLocation.productInventory >= quantity);
	}

	/**
	 * Handles the API call to Moz SOCi
	 * @param {string} endpoint
	 * @param  {Object} params
	 * @returns {Object}
	 */
	async request (params = {}) {
		const parameters = params;
		const cachekey = this.getCacheKey(JSON.stringify(parameters));
		const cachedResponse = this.getCache('bopisAPI', cachekey);

		if (cachedResponse) {
			return cachedResponse;
		}

		let locations = await this.getData();

		if (locations === false || !locations.length) {
			return false;
		}

		if (parameters.identifier) {
			locations = this.getStore(locations, parameters.identifier);
		}

		const userLat = parameters.latitude ?? null;
		const userLng = parameters.longitude ?? null;

		const formattedResponse = this.formatResponse(locations, userLat, userLng);

		if (parameters.limit > 0 && formattedResponse.length > parameters.limit) {
			formattedResponse.length = parameters.limit;
		}

		this.setCache('bopisAPI', cachekey, formattedResponse);

		return formattedResponse;
	}

	getStore (locations = [], identifier = '') {
		const formattedResponse = locations.filter((store) => {
			return store.properties.id?.toString() === identifier?.toString();
		});

		return formattedResponse;
	}

	async getData () {
		let response = this.rawLocationsCache?.data;
		if (!this.rawLocationsCache?.success) {
			const request = await fetch(`${this.storeLocatorPath}`);

			if (!request.ok) {
				throw Error(request.statusText);
			}

			response = await request.json();

			this.rawLocationsCache = {
				data: response,
				success: 1
			};
		}

		response = this.filterBySiteDomain(response);

		return response.features;
	}

	filterBySiteDomain (response) {
		return {
			...response,
			features: response.features.filter(feature => feature.site_domains?.includes(this.storeSiteDomain))
		};
	}

	/**
	 * Sets the Cache
	 * @param  {String} cacheName
	 * @param  {String} key
	 * @param  {String} value
	 */
	setCache (cacheName, key, value) {
		this.cache[cacheName].set(key, value);
	}

	/**
	 * Gets the Cache
	 * @param  {String}	cacheName
	 * @param  {String}	key
	 * @returns {Object|String}	Cache Value
	 */
	getCache (cacheName, key) {
		return this.cache[cacheName].get(key);
	}

	getCacheKey (jsonString) {
		try {
			const parsedObject = JSON.parse(jsonString);
			const sortedString = JSON.stringify(parsedObject, Object.keys(parsedObject).sort());

			let hash = Math.floor(Math.random() * 11);
			for (let i = 0; i < sortedString.length; i++) {
				// eslint-disable-next-line no-extra-parens
				hash = (hash * 32) + sortedString.charCodeAt(i);
			}

			const timestamp = Date.now().toString(36);
			const min = Math.pow(10, 10 - 1);
			const max = Math.pow(10, 10) - 1;
			const randomPart = Math.floor(Math.random() * (max - min + 1)) + min;
			const hashPart = Math.abs(hash).toString(16).slice(0, 16);

			const uniqueId = (timestamp + randomPart + hashPart).slice(0, 32);
			return uniqueId;
		} catch (error) {
			return `error-${error.message}`;
		}
	}

	/**
	 * Formats the response of each location from the flat GeoJSON file.
	 * Calculates distance from user's coordinates and sorts by miles away.
	 * @param  {Array}	response
	 * @param  {Number|null} userLat
	 * @param  {Number|null} userLng
	 * @returns {Array}
	 */
	formatResponse (response = [], userLat = null, userLng = null) {
		for (const store of response) {
			// eslint-disable-next-line array-element-newline
			const [storeLng, storeLat] = store.geometry.coordinates;

			store.identifier = store.properties.id;
			store.name = store.properties.name;
			store.properties.todaysHours = this.formatHours(store.properties);

			if (userLat !== null && userLng !== null) {
				store.milesAway = parseFloat(this.haversineDistance(userLat, userLng, storeLat, storeLng).toFixed(2));
			}
		}

		const formattedResponse = response.filter((store) => {
			const props = store.properties;

			// If site_domains is present, this domain must be in the list.
			if (props.site_domains?.length && !props.site_domains.includes(this.domain)) {
				return false;
			}

			// Exclude locations with a future opening date.
			if (props.opening_date && props.opening_date !== '0000-00-00') {
				const futureOpeningDate = new Date(props.opening_date);
				const today = new Date();
				futureOpeningDate.setHours(0, 0, 0, 0);
				today.setHours(0, 0, 0, 0);

				if (today.getTime() < futureOpeningDate.getTime()) {
					return false;
				}
			}

			return true;
		});

		if (userLat !== null && userLng !== null) {
			formattedResponse.sort((a, b) => a.milesAway - b.milesAway);
		}

		return formattedResponse;
	}

	/**
	 * Calculates the great-circle distance between two coordinates using the Haversine formula.
	 * @param  {Number} lat1
	 * @param  {Number} lng1
	 * @param  {Number} lat2
	 * @param  {Number} lng2
	 * @returns {Number} Distance in miles
	 */
	haversineDistance (lat1, lng1, lat2, lng2) {
		const R = 3958.8;
		const toRad = (deg) => deg * Math.PI / 180;
		const dLat = toRad(lat2 - lat1);
		const dLng = toRad(lng2 - lng1);
		const sinHalfDLat = Math.sin(dLat / 2);
		const sinHalfDLng = Math.sin(dLng / 2);
		const latTerm = sinHalfDLat * sinHalfDLat;
		const lngTerm = Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * sinHalfDLng * sinHalfDLng;
		const a = latTerm + lngTerm;

		return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	}

	/**
	 * Builds `properties.formattedHours` (all days) from the flat JSON hours object
	 * and returns today's hours entry.
	 * @param  {Object} location
	 * @returns {Object|null}
	 */
	formatHours (properties = {}) {
		let todaysHours = null;
		const timezone = properties.timezone;
		const storeDayOfWeek = new Date().toLocaleString('en-us', {
			timeZone: timezone,
			weekday: 'short'
		});
		const dayKeys = [
			'mon',
			'tue',
			'wed',
			'thu',
			'fri',
			'sat',
			'sun'
		];

		properties.formattedHours = {
			Fri: '',
			Mon: '',
			Sat: '',
			Sun: '',
			Thu: '',
			Tue: '',
			Wed: ''
		};

		dayKeys.forEach((dayKey, index) => {
			const dayLabel = this.apiDaysList[index];
			const hoursString = properties.hours?.[dayKey] ?? null;
			const specialHoursString = this.determineSpecialHours(properties.special_hours ?? {}, index + 1, timezone);
			const resolvedHours = specialHoursString === false ? hoursString : specialHoursString;
			const formattedHour = this.parseHoursString(resolvedHours);

			formattedHour.isToday = Boolean(storeDayOfWeek === dayLabel);
			properties.formattedHours[dayLabel] = formattedHour;

			if (formattedHour.isToday) {
				todaysHours = formattedHour;
			}
		});

		return todaysHours;
	}

	/**
	 * Returns the special-hours string for a given day (e.g. "10:00 AM - 2:00 PM"),
	 * or false if no special hours exist for that date.
	 * @param  {Object} specialOpeningHours - date-keyed object from the JSON
	 * @param  {Number} dayOfWeekIndex - 1 (Mon) through 7 (Sun)
	 * @param  {String} timezone - IANA timezone string from the store's properties
	 * @returns {String|false}
	 */
	determineSpecialHours (specialOpeningHours = {}, dayOfWeekIndex, timezone) {
		const now = new Date();
		const currentDayStr = now.toLocaleString('en-us', {
			timeZone: timezone,
			weekday: 'short'
		});
		const currentDayOfWeek = this.apiDaysList.indexOf(currentDayStr);
		const normalizedDay = (dayOfWeekIndex + 6) % 7;
		const difference = (normalizedDay - currentDayOfWeek + 7) % 7;
		const date = new Date(now);
		date.setDate(date.getDate() + difference);
		const formattedDate = date.toLocaleDateString('fr-CA', {
			day: '2-digit',
			month: '2-digit',
			timeZone: timezone,
			year: 'numeric'
		});

		return specialOpeningHours[formattedDate] ?? false;
	}

	/**
	 * Parses a flat JSON hours string into a display-ready object.
	 * Auto-detects the format by the presence of AM/PM:
	 *   - 12-hour (with AM/PM):  "10:00 AM - 9:00 PM"  (used as-is)
	 *   - 24-hour (no AM/PM):    "10:00-18:00"          (converted to 12h)
	 * @param  {String|null} hoursString
	 * @returns {Object}
	 */
	parseHoursString (hoursString = null) {
		if (!hoursString || hoursString.toLowerCase() === 'closed') {
			return {
				close: null,
				closed: true,
				formattedClose: null,
				formattedOpen: null,
				open: null
			};
		}

		// Detect format: 24h strings have no AM/PM; 12h strings do.
		const is24h = !(/AM|PM/i).test(hoursString);
		let close = null;
		let open = null;

		if (is24h) {
			const parts = hoursString.split('-');
			close = this.#formatTime(parts[1]?.trim());
			open = this.#formatTime(parts[0]?.trim());
		} else {
			const parts = hoursString.split(' - ');
			close = parts[1] ?? null;
			open = parts[0] ?? null;
		}

		return {
			close,
			closed: false,
			formattedClose: close,
			formattedOpen: open,
			open
		};
	}

	/**
	 * Converts a 24h time string ("18:00") to 12h AM/PM ("6:00 PM").
	 * @param  {String} time
	 * @returns {String|null}
	 */
	#formatTime (time) {
		if (!time) {
			return null;
		}

		const parts = time.split(':');
		let hour = parseInt(parts[0], 10);
		const minutes = parts[1] ?? '00';
		const suffix = hour < 12 ? 'AM' : 'PM';

		if (hour === 0) {
			hour = 12;
		}

		if (hour > 12) {
			hour -= 12;
		}

		return `${hour}:${minutes} ${suffix}`;
	}

	/**
	 * Conversion for Meters to Miles
	 * @param  {Number}	meters
	 * @return {Number}
	 */
	meterToMiles (meters = 0) {
		return meters / this.MI_TO_METER;
	}

	/**
	 * Slugify a string
	 * @param {String} text
	 * @returns {void}
	 */
	slugify (text) {
		return text?.toLowerCase().replace(/[^\w ]+/g, '').replace(/ +/g, '-');
	}

}
